// An app as the phone draws it: a rounded tile with the app's line icon, tinted
// per app (or plain), and its name underneath. Used by the home screens, the
// launcher and the dock picker, so an app looks the same wherever it is.
import { useRef, type ReactNode } from 'react'
import type { NavItem } from '@/lib/nav'
import { appHue, tintText, tokens } from '@/lib/colour'
import { cx } from '@/components/ui'
import { guardSelection, swallowNextClick, tick } from '@/lib/haptics'

export const AppGlyph = ({ app, size = 22 }: { app: NavItem; size?: number }) =>
  app.Icon ? (
    <app.Icon size={size} aria-hidden />
  ) : (
    <span className="text-xl leading-none">{app.icon}</span>
  )

export const AppTile = ({
  app,
  tint,
  active,
  className,
  size = 48,
}: {
  app: NavItem
  tint: 'colour' | 'mono'
  active?: boolean
  className?: string
  /** edge in CSS pixels (the layout's iconSize on Home and in the launcher) */
  size?: number
}) => {
  const hue = appHue(app.key)
  const t = tokens(hue)
  const tinted =
    tint === 'colour' && !active
      ? {
          // a wash of the app's hue over the panel, lit from the top like glass
          background: `linear-gradient(160deg, color-mix(in oklab, ${t.stripe} 22%, transparent), transparent 75%), var(--color-panel)`,
          color: tintText(hue),
          borderColor: `color-mix(in oklab, ${t.stripe} 30%, transparent)`,
        }
      : undefined
  // the size rides as a variable so the corner (.os-icon) scales with it, tighter in black
  const style = { ...tinted, width: size, height: size, '--icon-size': `${size}px` } as React.CSSProperties
  return (
    <span
      className={cx(
        'os-icon shrink-0 border flex items-center justify-center transition-colors shadow-[0_6px_18px_-10px_rgba(0,0,0,0.6)]',
        active ? 'bg-accent text-on-accent border-transparent' : 'bg-raised border-line text-fg-2',
        className,
      )}
      style={style}
    >
      <AppGlyph app={app} size={Math.round(size * 0.45)} />
    </span>
  )
}

export const AppLabel = ({ children, active }: { children: ReactNode; active?: boolean }) => (
  <span
    className={cx(
      'w-full truncate text-center text-[11px] leading-tight [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]',
      active ? 'text-fg font-medium' : 'text-fg-2',
    )}
  >
    {children}
  </span>
)

const HOLD_MS = 420
const SLOP = 10

/**
 * Press and hold: `onHold` fires after a moment unless the finger moved (then
 * it is a scroll or a drag, not a hold). `held()` tells the click handler to
 * ignore the click that ends a hold.
 */
export const useHold = (onHold: () => void, ms = HOLD_MS) => {
  const timer = useRef<number | null>(null)
  const origin = useRef<{ x: number; y: number } | null>(null)
  const fired = useRef(false)
  const cancel = () => {
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = null
  }
  return {
    held: () => {
      const f = fired.current
      fired.current = false
      return f
    },
    handlers: {
      onPointerDown: (e: React.PointerEvent) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return
        // a touch that may become a hold: no text anywhere may be selected until it lifts
        if (e.pointerType !== 'mouse') guardSelection()
        fired.current = false
        origin.current = { x: e.clientX, y: e.clientY }
        cancel()
        timer.current = window.setTimeout(() => {
          fired.current = true
          timer.current = null
          tick()
          // whatever opens now is under the finger: the click that ends this touch must not reach it
          swallowNextClick()
          onHold()
        }, ms)
      },
      onPointerMove: (e: React.PointerEvent) => {
        const o = origin.current
        if (timer.current && o && Math.hypot(e.clientX - o.x, e.clientY - o.y) > SLOP) cancel()
      },
      onPointerUp: cancel,
      onPointerCancel: cancel,
      onPointerLeave: cancel,
      onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
    },
  }
}
